import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Image } from 'expo-image'
import { useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { useResolvedAuthState } from '@/src/hooks/useResolvedAuthState'
import {
  ContentSession,
  libraryQueryOptions,
} from '@/src/queries/library-queries'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { AddFavoriteParams } from '../../../queries/favorite-queries'
import { AppScreenTopBar } from '../../../components/AppScreenTopBar'
import { ExerciseCard } from '../../../components/ExerciseCard'
import { PullToRefreshSectionList } from '../../../components/ui/PullToRefreshSectionList'
import {
  favoritesQueryOptions,
  useToggleFavorite,
} from '../../../queries/favorite-queries'
import {
  buildFavoriteProperties,
  MixpanelEvents,
  trackEvent,
} from '../../../services/analytics'
import { colors, spacing } from '../../../theme'
import { body1, label1 } from '../../../theme/typography'
import { showAlert } from '../../../utils/alert'
import { buildContentfulImageUrl } from '../../../utils/contentful-image'
import { LibrarySkeleton } from '../components/LibrarySkeleton'
import {
  buildLibrarySections,
  getErrorStatus,
  getSessionSubtitle,
  LibrarySection,
} from '../utils/library-screen-helpers'

const STAR_SUCCESS_SPINNER_DELAY_MS = 650

export default function Library() {
  const insets = useSafeAreaInsets()
  const { push } = useRouter()
  const { t } = useTranslation(['library', 'common'])
  const language = useContentfulLocale()
  const { getAccessToken } = useResolvedAuthState()

  const {
    data: libraryCategory,
    isLoading: isLoadingLibraryCategory,
    refetch: refetchLibraryCategory,
    isFetching: isRefreshingLibraryCategory,
  } = useSuspenseQuery(libraryQueryOptions(language))
  const { data: favoriteItems = [], isLoading: isLoadingFavorites } = useQuery(
    favoritesQueryOptions,
  )
  const { toggleFavorite } = useToggleFavorite()
  const [pendingFavoriteIds, setPendingFavoriteIds] = useState<Set<string>>(
    new Set(),
  )
  const hasTrackedViewRef = useRef(false)
  const isLoading = isLoadingFavorites || isLoadingLibraryCategory

  useEffect(() => {
    if (hasTrackedViewRef.current || isLoading) {
      return
    }

    hasTrackedViewRef.current = true
    trackEvent(MixpanelEvents.VideoViewed)
  }, [isLoading])

  const handleSessionPress = (session: ContentSession) => {
    push({
      pathname: '/session/[id]',
      params: {
        id: session.id,
        source: 'library',
      },
    })
  }

  const handleToggleFavorite = async (session: ContentSession) => {
    const contentfulId = session.id
    if (pendingFavoriteIds.has(contentfulId)) {
      return
    }

    setPendingFavoriteIds((prev) => {
      const next = new Set(prev)
      next.add(contentfulId)
      return next
    })

    const favoriteItem: AddFavoriteParams = {
      userId: 0, // Backend identifies user via token
      contentfulType: 'session',
      contentfulId,
    }
    const wasFavorite = favoriteItems.some(
      (item) => item.contentfulId === contentfulId,
    )
    const sessionName = session.title ?? ''
    const favoriteEvent = wasFavorite
      ? MixpanelEvents.FavouriteRemoved
      : MixpanelEvents.FavouriteSaved

    try {
      await getAccessToken()
      await toggleFavorite(contentfulId, favoriteItem)
      trackEvent(
        favoriteEvent,
        buildFavoriteProperties({
          contentfulId,
          sessionName,
        }),
      )
      await new Promise((resolve) =>
        setTimeout(resolve, STAR_SUCCESS_SPINNER_DELAY_MS),
      )
    } catch (error: unknown) {
      console.error('Failed to toggle favorite', error)
      // TODO: Investigate persistent 403 Forbidden error on bookmark mutations.
      // Likely related to backend user state sync after Auth0 signup.
      const errorStatus = getErrorStatus(error)
      const errorMsg =
        errorStatus === 403
          ? t('library:error_forbidden_account_sync')
          : t('library:error_toggle_favorite')
      showAlert(t('common:error'), errorMsg)
    }

    setPendingFavoriteIds((prev) => {
      const next = new Set(prev)
      next.delete(contentfulId)
      return next
    })
  }

  const sections = useMemo(() => {
    const favoriteContentIds = new Set<string>()
    for (const favoriteItem of favoriteItems) {
      if (favoriteItem.contentfulId != null) {
        favoriteContentIds.add(favoriteItem.contentfulId)
      }
    }

    return buildLibrarySections({
      sessions: libraryCategory?.sessions ?? [],
      favoriteContentIds,
      featuredTitle: t('library:featured_exercises'),
      libraryTitle: t('library:exercise_library'),
    })
  }, [favoriteItems, libraryCategory?.sessions, t])

  if (isLoading) {
    return <LibrarySkeleton />
  }

  const renderItem = ({
    item: session,
    section,
  }: {
    item: ContentSession
    section: LibrarySection
  }) => {
    const sessionTitle = session.title ?? ''

    const iconUrl = buildContentfulImageUrl(
      session.category?.icon?.url,
      session.category?.icon?.contentType ?? '',
      'icon',
    )
    const isFavorite = favoriteItems.some(
      (item) => item.contentfulId === session.id,
    )
    const icon =
      iconUrl != null ? (
        <Image
          source={{ uri: iconUrl }}
          style={{ width: 32, height: 32 }}
          contentFit="contain"
        />
      ) : null
    return (
      <View style={{ paddingHorizontal: 17.5 }}>
        <ExerciseCard
          title={sessionTitle}
          subtitle={getSessionSubtitle(session)}
          icon={icon}
          isFeatured={section.isFeatured}
          isFavorite={isFavorite}
          isFavoriteLoading={pendingFavoriteIds.has(session.id)}
          onPress={() => handleSessionPress(session)}
          onFavoritePress={() => handleToggleFavorite(session)}
        />
      </View>
    )
  }

  const renderSectionHeader = ({ section }: { section: LibrarySection }) => {
    return (
      <View
        style={{
          paddingHorizontal: 17.5,
          marginTop: section.isFeatured ? 0 : 30,
          marginBottom: spacing.headerContentGap,
        }}
      >
        <Text style={[body1, { color: colors.textHeader }]}>
          {section.title}
        </Text>
        {section.isFeatured && section.data.length === 0 && (
          <View className="bg-background-secondary p-6 rounded-xl border border-border items-center justify-center mt-4">
            <Text
              style={[label1, { color: colors.textBody, textAlign: 'center' }]}
            >
              {t('library:add_featured_placeholder')}
            </Text>
          </View>
        )}
      </View>
    )
  }

  return (
    <View
      className="flex-1"
      style={{ backgroundColor: colors.backgroundPrimary }}
    >
      <Head>
        <title>{t('common:page_title_library')}</title>
      </Head>
      <AppScreenTopBar />

      <PullToRefreshSectionList
        refreshing={isRefreshingLibraryCategory}
        onRefresh={async () => {
          await refetchLibraryCategory()
        }}
        refreshControlProps={{
          colors: [colors.primary],
          tintColor: colors.primary,
        }}
        sections={sections}
        extraData={pendingFavoriteIds}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        scrollEventThrottle={16}
        stickySectionHeadersEnabled={false}
        contentContainerStyle={{ paddingBottom: 40 + insets.bottom }}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View className="flex-1 justify-center items-center py-20 px-10">
            <Text className="text-gray-500 text-center text-base">
              {t('library:no_exercises_found')}
            </Text>
          </View>
        }
      />
    </View>
  )
}
